import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  PersonAdd,
  ShoppingCart,
  TrendingUp,
  LoginSharp,
  DateRange,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { backofficeManagementService } from '../../services/backofficeManagementService';

interface DashboardAnalytics {
  date_range: {
    start_date: string;
    end_date: string;
  };
  signup_requests: Array<{ date: string; value: number }>;
  customer_orders: Array<{ date: string; value: number }>;
  customer_signins: Array<{ date: string; value: number }>;
  sales_trend: Array<{ date: string; orders: number; sales: number }>;
}

interface StatCard {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  change?: string;
}

const BackofficeDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Date range state - default to current week
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(6, 'day'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());

  useEffect(() => {
    loadAnalytics();
  }, [startDate, endDate]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await backofficeManagementService.getDashboardAnalytics(
        startDate.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD')
      );
      setAnalytics(data);
    } catch (error: any) {
      setError('Failed to load analytics: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('MMM DD');
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const getStatCards = (): StatCard[] => {
    if (!analytics) return [];

    const totalSignupRequests = analytics.signup_requests.reduce((sum, item) => sum + item.value, 0);
    const totalOrders = analytics.customer_orders.reduce((sum, item) => sum + item.value, 0);
    const totalSignins = analytics.customer_signins.reduce((sum, item) => sum + item.value, 0);
    const totalSales = analytics.sales_trend.reduce((sum, item) => sum + item.sales, 0);

    return [
      {
        title: t('backoffice.dashboard.signupRequests'),
        value: totalSignupRequests,
        icon: <PersonAdd sx={{ fontSize: 40 }} />,
        color: '#1976d2',
      },
      {
        title: t('backoffice.dashboard.customerOrders'),
        value: totalOrders,
        icon: <ShoppingCart sx={{ fontSize: 40 }} />,
        color: '#388e3c',
      },
      {
        title: t('backoffice.dashboard.customerSignins'),
        value: totalSignins,
        icon: <LoginSharp sx={{ fontSize: 40 }} />,
        color: '#f57c00',
      },
      {
        title: t('backoffice.dashboard.totalSales'),
        value: totalSales,
        icon: <TrendingUp sx={{ fontSize: 40 }} />,
        color: '#7b1fa2',
      },
    ];
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  const statCards = getStatCards();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('backoffice.dashboard.title')}
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
          {t('backoffice.dashboard.subtitle')}
        </Typography>

        {/* Date Range Picker */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <DateRange color="primary" />
            <Typography variant="h6">{t('backoffice.dashboard.dateFilter')}</Typography>
            <DatePicker
              label={t('backoffice.dashboard.startDate')}
              value={startDate}
              onChange={(newValue) => newValue && setStartDate(newValue)}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label={t('backoffice.dashboard.endDate')}
              value={endDate}
              onChange={(newValue) => newValue && setEndDate(newValue)}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: 'small' } }}
            />
            <Chip 
              label={`${dayjs(analytics?.date_range.start_date).format('DD MMM')} - ${dayjs(analytics?.date_range.end_date).format('DD MMM YYYY')}`}
              color="primary"
              variant="outlined"
            />
          </Box>
        </Paper>

        {analytics && (
          <>
            {/* Summary Cards */}
            <Box display="flex" flexWrap="wrap" gap={3} sx={{ mb: 4 }}>
              {statCards.map((card, index) => (
                <Box key={index} sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography color="textSecondary" gutterBottom variant="h6">
                            {card.title}
                          </Typography>
                          <Typography variant="h4" component="div" sx={{ color: card.color }}>
                            {card.title.includes('Penjualan') ? formatCurrency(card.value) : card.value.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ color: card.color }}>
                          {card.icon}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>

            {/* Charts Grid */}
            <Box display="flex" flexWrap="wrap" gap={3}>
              
              {/* Sign-up Requests Chart */}
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('backoffice.dashboard.charts.signupRequestsTrend')}
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={analytics.signup_requests}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={formatDate}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis />
                      <Tooltip 
                        labelFormatter={(value) => dayjs(value).format('DD MMMM YYYY')}
                        formatter={(value: number | undefined) => [value || 0, t('backoffice.dashboard.tooltips.requests')]}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#1976d2" 
                        fill="#1976d2"
                        fillOpacity={0.3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Paper>
              </Box>

              {/* Customer Orders Chart */}
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('backoffice.dashboard.charts.customerOrdersTrend')}
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics.customer_orders}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={formatDate}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis />
                      <Tooltip 
                        labelFormatter={(value) => dayjs(value).format('DD MMMM YYYY')}
                        formatter={(value: number | undefined) => [value || 0, t('backoffice.dashboard.tooltips.orders')]}
                      />
                      <Bar dataKey="value" fill="#388e3c" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Box>

              {/* Customer Sign-in Trends */}
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('backoffice.dashboard.charts.customerSigninsTrend')}
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analytics.customer_signins}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={formatDate}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis />
                      <Tooltip 
                        labelFormatter={(value) => dayjs(value).format('DD MMMM YYYY')}
                        formatter={(value: number | undefined) => [value || 0, t('backoffice.dashboard.tooltips.signins')]}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#f57c00" 
                        strokeWidth={3}
                        dot={{ fill: '#f57c00', strokeWidth: 2, r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Box>

              {/* Sales Trend Chart */}
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('backoffice.dashboard.charts.salesTrend')}
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analytics.sales_trend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={formatDate}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip 
                        labelFormatter={(value) => dayjs(value).format('DD MMMM YYYY')}
                        formatter={(value: number | undefined, name: string | undefined) => [
                          name === 'sales' ? formatCurrency(value || 0) : (value || 0),
                          name === 'sales' ? t('backoffice.dashboard.tooltips.salesIDR') : t('backoffice.dashboard.tooltips.sales')
                        ]}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey="orders" fill="#7b1fa2" name={t('backoffice.dashboard.tooltips.orders')} />
                      <Line 
                        yAxisId="right" 
                        type="monotone" 
                        dataKey="sales" 
                        stroke="#d32f2f" 
                        strokeWidth={3}
                        name={t('backoffice.dashboard.tooltips.salesIDR')}
                        dot={{ fill: '#d32f2f', strokeWidth: 2, r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Box>

            </Box>
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default BackofficeDashboardPage;